import React, { useEffect, useState } from "react";
import { api } from "./utils/api";

interface ITweetResult {
  url: string;
  source: string;
  content: string;
  created_at: string;
}

const STORAGE_KEY = "twitter_realtime_form";

interface FormState {
  query: string;
  after: string;
  until: string;
  searchType: "Latest" | "Top";
  verified: boolean;
}

function loadForm(): FormState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { query: "", after: "", until: "", searchType: "Latest", verified: false };
}

function saveForm(state: FormState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function buildTwitterUrl(query: string, after: string, until: string, verified: boolean, searchType: "Latest" | "Top"): string {
  let q = query.trim();
  if (after) q += ` after:${after}`;
  if (until) q += ` until:${until}`;
  if (verified) q += " filter:blue_verified";
  const params = new URLSearchParams({ q, src: "typed_query" });
  if (searchType === "Latest") params.set("f", "live");
  return `https://x.com/search?${params.toString()}`;
}

function formatTwitterDate(raw: string): string {
  try {
    return new Date(raw).toLocaleString("en-US", {
      timeZone: "America/New_York",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return raw;
  }
}

export const TwitterRealtime = (props: { query?: string }) => {
  const saved = loadForm();
  // opened with a query (e.g. the chart's Twitter button): use it instead of the saved one
  const [query, setQuery] = useState(props.query ?? saved.query);
  const [after, setAfter] = useState(saved.after);
  const [until, setUntil] = useState(saved.until);
  const [searchType, setSearchType] = useState<"Latest" | "Top">(saved.searchType);
  const [verified, setVerified] = useState(saved.verified);

  useEffect(() => {
    saveForm({ query, after, until, searchType, verified });
  }, [query, after, until, searchType, verified]);

  const [tweets, setTweets] = useState<ITweetResult[]>([]);
  const [nextCursor, setNextCursor] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);

  function doSearch(cursor: string, append: boolean) {
    if (!query.trim()) return;
    setIsLoading(true);
    setError("");

    api
      .post("/twitter-realtime-search", {
        query: query.trim(),
        after,
        until,
        search_type: searchType,
        cursor,
        verified,
      })
      .then((res) => {
        const data = res.data;
        setTweets(append ? (prev) => [...prev, ...data.tweets] : data.tweets);
        setNextCursor(data.next_cursor ?? "");
        setSearched(true);
      })
      .catch((err) => {
        setError(err?.response?.data?.detail ?? "Search failed");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  // ...and run that search right away
  useEffect(() => {
    if (props.query) doSearch("", false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    doSearch("", false);
  }

  function handleLoadMore() {
    doSearch(nextCursor, true);
  }

  return (
    <div className="p-4 flex flex-col gap-3 h-full overflow-hidden">
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
        <div>
          <label htmlFor="twitter-realtime-search-query">Query</label>
          <input
            id="twitter-realtime-search-query"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Query"
            className="border rounded px-2 py-1 flex-1"
          />
        </div>

        <div>
          <label htmlFor="twitter-realtime-search-order">Order</label>
          <select
            id="twitter-realtime-search-order"
            value={searchType}
            onChange={(e) => setSearchType(e.target.value as "Latest" | "Top")}
            className="border rounded px-2 py-1"
          >
            <option value="Latest">Latest</option>
            <option value="Top">Top</option>
          </select>
        </div>

        <div>
          <label className="text-sm text-gray-500 w-12">After</label>
          <input
            type="text"
            placeholder="YYYY-MM-DD"
            value={after}
            onChange={(e) => setAfter(e.target.value)}
            className="border rounded px-2 py-1"
          />
        </div>

        <div>
          <label className="text-sm text-gray-500 w-12">Until</label>
          <input
            type="text"
            placeholder="YYYY-MM-DD"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            className="border rounded px-2 py-1"
          />
        </div>

        <div className="flex items-end pb-1">
          <label className="flex items-center gap-1 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={verified}
              onChange={(e) => setVerified(e.target.checked)}
            />
            Verified
          </label>
        </div>

        <div>
          <button type="submit" className="btn btn-default sm:ml-auto sm:mt-5">
            {isLoading ? "Searching…" : "Search"}
          </button>
        </div>
      </form>

      {query.trim() && (
        <a
          href={buildTwitterUrl(query, after, until, verified, searchType)}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-blue-500 self-start"
        >
          Open on Twitter ↗
        </a>
      )}

      {error && <div className="text-red-500 text-sm">{error}</div>}

      <div className="flex-1 overflow-auto flex flex-col gap-2">
        {searched && tweets.length === 0 && !isLoading && (
          <div className="text-gray-400 text-sm">No results.</div>
        )}

        {tweets.map((tweet) => (
          <div key={tweet.url} className="border-b pb-2">
            <div className="flex gap-2 items-baseline mb-1">
              <span className="font-semibold text-sm">@{tweet.source}</span>
              <span className="text-xs text-gray-400">
                {formatTwitterDate(tweet.created_at)}
              </span>
              <a
                href={tweet.url}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-gray-500"
              >
                (source)
              </a>
            </div>
            <p className="text-sm whitespace-pre-wrap">{tweet.content}</p>
          </div>
        ))}

        {nextCursor && !isLoading && (
          <button
            onClick={handleLoadMore}
            className="btn btn-default mt-2 self-start"
          >
            Load more
          </button>
        )}

        {isLoading && tweets.length > 0 && (
          <div className="text-gray-400 text-sm">Loading…</div>
        )}
      </div>
    </div>
  );
};
